import { format } from 'd3-format'

/**
 * Shared chart styling constants. Everything resolves to the semantic
 * CSS custom-property contract (tokens.css) — never raw hex — so all
 * charts re-theme automatically in dark mode.
 */

export type ChartSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info'

/** Display order — most severe first. Matches the badge/graph grammar. */
export const SEVERITY_ORDER: readonly ChartSeverity[] = [
  'critical',
  'high',
  'medium',
  'low',
  'info',
]

export function severityToken(severity: ChartSeverity): string {
  return `var(--sev-${severity})`
}

export function severityBgToken(severity: ChartSeverity): string {
  return `var(--sev-${severity}-bg)`
}

/** Categorical palette for multi-series charts (max 5–7 series per doctrine). */
export const VIZ_TOKENS: readonly string[] = [
  'var(--viz-1)',
  'var(--viz-2)',
  'var(--viz-3)',
  'var(--viz-4)',
  'var(--viz-5)',
  'var(--viz-6)',
  'var(--viz-7)',
  'var(--viz-8)',
]

export function vizToken(index: number): string {
  return VIZ_TOKENS[((index % VIZ_TOKENS.length) + VIZ_TOKENS.length) % VIZ_TOKENS.length]
}

/** Recharts `tick` style for axes — small, quiet, no axis lines. */
export const AXIS_TICK = {
  fontSize: 11,
  fill: 'var(--text-faint)',
  fontFamily: 'var(--ff-ui)',
} as const

/** Recharts `tick` style for category labels that must stay readable. */
export const CATEGORY_TICK = {
  fontSize: 11,
  fill: 'var(--text-soft)',
  fontFamily: 'var(--ff-ui)',
} as const

export const GRID_STROKE = 'var(--border)'
export const GRID_DASH = '3 3'

/** Thousands-separated integer: 1234 → "1,234". */
export const formatCount = format(',')

/** SI-abbreviated: 12400 → "12.4k". */
export const formatSI = format('~s')

/** Signed delta: 12 → "+12", -3 → "−3" (true minus sign). */
export function formatSignedCount(value: number): string {
  if (value > 0) return `+${formatCount(value)}`
  if (value < 0) return `−${formatCount(Math.abs(value))}`
  return '0'
}

/** `useId()` emits colons that are invalid inside `url(#…)` refs. */
export function svgSafeId(rawId: string): string {
  return `it-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`
}
